import React, { useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Modal,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import theme from '../../theme';

// Dynamic XLSX import to avoid webpack issues - only for native platforms
const loadXLSX = async () => {
  try {
    if (typeof window !== 'undefined') {
      // Web environment - Excel functionality disabled to avoid compilation issues
      console.warn('Excel functionality is disabled in web environment');
      return null;
    } else {
      // Native environment - use require instead of import
      const XLSX = require('xlsx');
      return XLSX;
    }
  } catch (error) {
    console.warn('XLSX library not available:', error);
    return null;
  }
};

interface ColumnMapping {
  [key: string]: string; // Excel column name -> database field name
}

interface ExcelUploadProps {
  title: string;
  description: string;
  expectedColumns: string[]; // Expected database field names
  columnMappings: ColumnMapping; // Suggested mappings
  onDataProcessed: (data: any[]) => Promise<void>;
  sampleData?: any; // Sample data structure for preview
}

interface ProcessingProgress {
  phase: 'idle' | 'reading' | 'parsing' | 'processing' | 'uploading' | 'completed' | 'error';
  progress: number;
  message: string;
  details?: string;
}

export const ExcelUploadComponent: React.FC<ExcelUploadProps> = ({
  title,
  description,
  expectedColumns,
  columnMappings,
  onDataProcessed,
  sampleData,
}) => {
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [processing, setProcessing] = useState<ProcessingProgress>({
    phase: 'idle',
    progress: 0,
    message: 'Ready to upload Excel file',
  });
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [detectedColumns, setDetectedColumns] = useState<string[]>([]);
  const [mappingModalVisible, setMappingModalVisible] = useState(false);
  const [finalColumnMapping, setFinalColumnMapping] = useState<ColumnMapping>({});

  const pickExcelFile = async () => {
    // Check if running in web environment
    if (typeof window !== 'undefined') {
      Alert.alert(
        'Excel Upload Not Available', 
        'Excel file upload functionality is currently available only in the mobile app. Please use the mobile version for Excel import features.'
      );
      return;
    }

    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-excel',
          'application/vnd.ms-excel.sheet.macroEnabled.12',
          'text/csv'
        ],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets[0]) {
        const file = result.assets[0];
        setSelectedFile(file);
        setProcessing({
          phase: 'reading',
          progress: 10,
          message: `Selected: ${file.name}`,
          details: `File size: ${(file.size || 0 / 1024).toFixed(1)} KB`
        });
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to select file');
      console.error('File picker error:', error);
    }
  };

  const parseExcelFile = async () => {
    if (!selectedFile) return;

    setProcessing({
      phase: 'parsing',
      progress: 20,
      message: 'Reading Excel file...',
      details: 'Loading file content into memory'
    });

    try {
      const XLSX = await loadXLSX();
      if (!XLSX) {
        throw new Error('Excel processing is not available in this environment');
      }

      let workbook: any;

      if (selectedFile.name.endsWith('.csv')) {
        // Handle CSV files
        const response = await fetch(selectedFile.uri);
        const text = await response.text();
        workbook = XLSX.read(text, { type: 'string' });
      } else {
        // Handle Excel files
        const response = await fetch(selectedFile.uri);
        const arrayBuffer = await response.arrayBuffer();
        workbook = XLSX.read(arrayBuffer, { type: 'array' });
      }

      setProcessing({
        phase: 'parsing',
        progress: 40,
        message: 'Parsing Excel data...',
        details: 'Converting Excel sheets to JSON format'
      });

      // Get the first worksheet
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // Convert to JSON
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
        header: 1,
        defval: '',
        blankrows: false
      });

      if (jsonData.length === 0) {
        throw new Error('Excel file appears to be empty');
      }

      // Extract headers from first row
      const headers = (jsonData[0] as string[]).map(h => String(h).trim()).filter(h => h);
      setDetectedColumns(headers);

      // Extract data rows
      const dataRows = jsonData.slice(1).filter((row: any[]) => 
        row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== '')
      );

      setProcessing({
        phase: 'parsing',
        progress: 60,
        message: `Parsed ${dataRows.length} rows`,
        details: `Found columns: ${headers.join(', ')}`
      });

      // Convert data to objects with headers as keys
      const processedData = dataRows.map((row: any[], index: number) => {
        const obj: any = { _rowNumber: index + 2 }; // +2 because we skip header and start from 1
        headers.forEach((header, colIndex) => {
          obj[header] = row[colIndex] || '';
        });
        return obj;
      });

      setParsedData(processedData);

      // Auto-map columns
      const autoMapping: ColumnMapping = {};
      headers.forEach(header => {
        const normalizedHeader = header.toLowerCase().replace(/[^a-z0-9]/g, '');
        
        // Try to find exact match first
        const exactMatch = Object.keys(columnMappings).find(key => 
          key.toLowerCase().replace(/[^a-z0-9]/g, '') === normalizedHeader
        );
        
        if (exactMatch) {
          autoMapping[header] = columnMappings[exactMatch];
        } else {
          // Try partial matches
          const partialMatch = Object.keys(columnMappings).find(key => 
            normalizedHeader.includes(key.toLowerCase().replace(/[^a-z0-9]/g, '')) ||
            key.toLowerCase().replace(/[^a-z0-9]/g, '').includes(normalizedHeader)
          );
          
          if (partialMatch) {
            autoMapping[header] = columnMappings[partialMatch];
          }
        }
      });

      setFinalColumnMapping(autoMapping);
      setMappingModalVisible(true);

      setProcessing({
        phase: 'processing',
        progress: 80,
        message: 'Data parsing completed',
        details: `Ready to map ${headers.length} columns`
      });

    } catch (error) {
      setProcessing({
        phase: 'error',
        progress: 0,
        message: 'Failed to parse Excel file',
        details: error instanceof Error ? error.message : 'Unknown error occurred'
      });
      Alert.alert('Parse Error', `Failed to parse Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const processMappedData = async () => {
    setMappingModalVisible(false);
    setProcessing({
      phase: 'processing',
      progress: 85,
      message: 'Processing mapped data...',
      details: 'Converting Excel data to database format'
    });

    try {
      // Transform data using the final column mapping
      const transformedData = parsedData.map(row => {
        const transformedRow: any = {};
        
        Object.entries(finalColumnMapping).forEach(([excelColumn, dbField]) => {
          if (dbField && row[excelColumn] !== undefined) {
            transformedRow[dbField] = row[excelColumn];
          }
        });

        // Add row number for error tracking
        transformedRow._rowNumber = row._rowNumber;
        
        return transformedRow;
      });

      setProcessing({
        phase: 'uploading',
        progress: 90,
        message: 'Uploading to database...',
        details: `Processing ${transformedData.length} records`
      });

      // Call the provided data processing function
      await onDataProcessed(transformedData);

      setProcessing({
        phase: 'completed',
        progress: 100,
        message: 'Upload completed successfully!',
        details: `${transformedData.length} records processed`
      });

      // Reset after success
      setTimeout(() => {
        setSelectedFile(null);
        setParsedData([]);
        setDetectedColumns([]);
        setFinalColumnMapping({});
        setProcessing({
          phase: 'idle',
          progress: 0,
          message: 'Ready to upload Excel file',
        });
      }, 3000);

    } catch (error) {
      setProcessing({
        phase: 'error',
        progress: 0,
        message: 'Upload failed',
        details: error instanceof Error ? error.message : 'Unknown error occurred'
      });
      Alert.alert('Upload Error', `Failed to upload data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const downloadTemplate = async () => {
    if (!sampleData) return;

    try {
      const XLSX = await loadXLSX();
      if (!XLSX) {
        Alert.alert('Error', 'Excel functionality is not available in this environment');
        return;
      }

      // Create a new workbook
      const wb = XLSX.utils.book_new();
      
      // Create worksheet with sample data
      const ws = XLSX.utils.json_to_sheet([sampleData]);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Template');
      
      // Write the file
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      
      // Create blob and download (Web only)
      if (typeof window !== 'undefined') {
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${title.toLowerCase().replace(/\s+/g, '_')}_template.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to generate template');
      console.error('Template generation error:', error);
    }
  };

  const renderColumnMappingModal = () => (
    <Modal
      visible={mappingModalVisible}
      animationType="slide"
      presentationStyle="pageSheet"
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Column Mapping</Text>
          <TouchableOpacity
            onPress={() => setMappingModalVisible(false)}
            style={styles.closeButton}
          >
                        <Ionicons name="close" size={24} color={theme.colors.foreground} />
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalContent}>
          <Text style={styles.mappingDescription}>
            Map Excel columns to database fields. Auto-detected mappings are shown below.
          </Text>

          {detectedColumns.map(column => (
            <View key={column} style={styles.mappingRow}>
              <View style={styles.excelColumn}>
                <Text style={styles.columnTitle}>Excel Column:</Text>
                <Text style={styles.columnValue}>{column}</Text>
              </View>
              
              <Ionicons name="arrow-forward" size={20} color={theme.colors.primary} />
              
              <View style={styles.dbField}>
                <Text style={styles.columnTitle}>Database Field:</Text>
                <Text style={[
                  styles.columnValue,
                  finalColumnMapping[column] ? styles.mappedField : styles.unmappedField
                ]}>
                  {finalColumnMapping[column] || 'Not mapped'}
                </Text>
              </View>
            </View>
          ))}

          <View style={styles.previewSection}>
            <Text style={styles.previewTitle}>Data Preview (First 3 rows):</Text>
            {parsedData.slice(0, 3).map((row, index) => (
              <View key={index} style={styles.previewRow}>
                <Text style={styles.previewRowTitle}>Row {index + 1}:</Text>
                {Object.entries(finalColumnMapping).map(([excelColumn, dbField]) => (
                  dbField && (
                    <Text key={excelColumn} style={styles.previewData}>
                      {dbField}: {String(row[excelColumn] || '').substring(0, 30)}
                      {String(row[excelColumn] || '').length > 30 ? '...' : ''}
                    </Text>
                  )
                ))}
              </View>
            ))}
          </View>
        </ScrollView>

        <View style={styles.modalFooter}>
          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={() => setMappingModalVisible(false)}
          >
            <Text style={styles.secondaryButtonText}>Cancel</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.primaryButton}
            onPress={processMappedData}
          >
            <Text style={styles.primaryButtonText}>Process Data</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  const getProgressColor = () => {
    switch (processing.phase) {
      case 'error': return '#EF4444';
      case 'completed': return '#10B981';
      default: return theme.colors.primary;
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.description}>{description}</Text>
      </View>

      {/* Template Download */}
      {sampleData && (
        <TouchableOpacity style={styles.templateButton} onPress={downloadTemplate}>
          <Ionicons name="download-outline" size={20} color={theme.colors.primary} />
          <Text style={styles.templateButtonText}>Download Template</Text>
        </TouchableOpacity>
      )}

      {/* File Selection */}
      <TouchableOpacity 
        style={styles.fileButton} 
        onPress={pickExcelFile}
        disabled={processing.phase === 'processing' || processing.phase === 'uploading'}
      >
        <Ionicons 
          name="document-outline" 
          size={24} 
          color={selectedFile ? theme.colors.success : theme.colors.primary} 
        />
        <Text style={[styles.fileButtonText, selectedFile && styles.fileSelectedText]}>
          {selectedFile ? selectedFile.name : 'Select Excel File (.xlsx, .xls, .csv)'}
        </Text>
      </TouchableOpacity>

      {/* Process Button */}
      {selectedFile && processing.phase === 'reading' && (
        <TouchableOpacity style={styles.processButton} onPress={parseExcelFile}>
          <Text style={styles.processButtonText}>Parse Excel File</Text>
        </TouchableOpacity>
      )}

      {/* Progress */}
      {processing.phase !== 'idle' && (
        <View style={styles.progressContainer}>
          <View style={styles.progressHeader}>
            <Text style={styles.progressMessage}>{processing.message}</Text>
            {processing.phase === 'processing' || processing.phase === 'uploading' ? (
              <ActivityIndicator size="small" color={getProgressColor()} />
            ) : null}
          </View>
          
          <View style={styles.progressBarContainer}>
            <View 
              style={[
                styles.progressBar, 
                { 
                  width: `${processing.progress}%`,
                  backgroundColor: getProgressColor()
                }
              ]} 
            />
          </View>
          
          {processing.details && (
            <Text style={styles.progressDetails}>{processing.details}</Text>
          )}
        </View>
      )}

      {/* Expected Columns Info */}
      <View style={styles.infoSection}>
        <Text style={styles.infoTitle}>Expected Columns:</Text>
        <View style={styles.columnsList}>
          {expectedColumns.map(column => (
            <View key={column} style={styles.columnChip}>
              <Text style={styles.columnChipText}>{column}</Text>
            </View>
          ))}
        </View>
      </View>

      {renderColumnMappingModal()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: '#f9fafb',
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.foreground,
    marginBottom: 8,
  },
  description: {
    fontSize: 16,
    color: theme.colors.muted,
    lineHeight: 22,
  },
  templateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f9ff',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: theme.colors.primary,
  },
  templateButtonText: {
    marginLeft: 8,
    color: theme.colors.primary,
    fontWeight: '600',
  },
  fileButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#e5e7eb',
    borderStyle: 'dashed',
    marginBottom: 16,
  },
  fileButtonText: {
    marginLeft: 12,
    fontSize: 16,
    color: theme.colors.muted,
  },
  fileSelectedText: {
    color: theme.colors.success,
    fontWeight: '600',
  },
  processButton: {
    backgroundColor: theme.colors.primary,
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 16,
  },
  processButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  progressContainer: {
    backgroundColor: 'white',
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressMessage: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.foreground,
  },
  progressBarContainer: {
    height: 8,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
    marginBottom: 8,
  },
  progressBar: {
    height: '100%',
    borderRadius: 4,
  },
  progressDetails: {
    fontSize: 14,
    color: theme.colors.muted,
  },
  infoSection: {
    backgroundColor: 'white',
    padding: 16,
    borderRadius: 8,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.foreground,
    marginBottom: 12,
  },
  columnsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  columnChip: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  columnChipText: {
    fontSize: 12,
    color: theme.colors.muted,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'white',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.foreground,
  },
  closeButton: {
    padding: 8,
  },
  modalContent: {
    flex: 1,
    padding: 16,
  },
  mappingDescription: {
    fontSize: 14,
    color: theme.colors.muted,
    marginBottom: 16,
    lineHeight: 20,
  },
  mappingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  excelColumn: {
    flex: 1,
  },
  dbField: {
    flex: 1,
  },
  columnTitle: {
    fontSize: 12,
    color: theme.colors.muted,
    marginBottom: 2,
  },
  columnValue: {
    fontSize: 14,
    fontWeight: '600',
  },
  mappedField: {
    color: theme.colors.success,
  },
  unmappedField: {
    color: theme.colors.error,
  },
  previewSection: {
    marginTop: 24,
    padding: 16,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
  },
  previewTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.foreground,
    marginBottom: 12,
  },
  previewRow: {
    marginBottom: 12,
    padding: 8,
    backgroundColor: 'white',
    borderRadius: 4,
  },
  previewRowTitle: {
    fontSize: 12,
    color: theme.colors.muted,
    marginBottom: 4,
  },
  previewData: {
    fontSize: 12,
    color: theme.colors.foreground,
    marginBottom: 2,
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  secondaryButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    alignItems: 'center',
    marginRight: 8,
  },
  secondaryButtonText: {
    color: theme.colors.muted,
    fontWeight: '600',
  },
  primaryButton: {
    flex: 1,
    backgroundColor: theme.colors.primary,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginLeft: 8,
  },
  primaryButtonText: {
    color: 'white',
    fontWeight: '600',
  },
});
